<script lang="ts">
  import { onMount } from "svelte";
  import { Tabs, TabItem, Button } from "flowbite-svelte";
  import ResultsTree from "./ResultsTree.svelte";
  import ResultsSearch from "./ResultsSearch.svelte";
  import ChatHistory from "./ChatHistory.svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faFolderTree, faMagnifyingGlass, faPencil, faCommentDots } from "@fortawesome/free-solid-svg-icons";
  import { userStore } from "../../../services/stores/user-store.js";
  import { saveUserMetadata, getCustomer } from "../../../services/api/data-handler.js";
  import { createEventDispatcher } from "svelte";
  import { get } from "svelte/store";
  import type { Customer } from "../../../services/api/types.js";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";

  const dispatch = createEventDispatcher();
  export let customer_id: string;

  let canManageCustomers = false;
  let customer: Customer;
  let isCustomerLoaded = false;

  let currentExplorer: "browse" | "search" | "your_chats" = "browse";

  async function initializeCustomer() {
    try {
      const customerData = await getCustomer(customer_id);
      if (customerData?.customer) {
        customer = customerData.customer;
      }
      isCustomerLoaded = true;
    } catch (error) {
      logError(error, "ResultsExplorer", undefined, { message: "Error fetching customer" });
    }
  }

  $: if (customer_id) {
    initializeCustomer();
  }

  onMount(async () => {
    const store = get(userStore);
    canManageCustomers = store.claims?.canManageCustomers ?? false;
    if (store.user?.metadata?.results_explorer?.active) {
      currentExplorer = store.user.metadata.results_explorer.active;
      dispatch("showHelper", currentExplorer);
    }
  });

  async function onTabChange(newTab: "search" | "browse" | "your_chats") {
    try {
      const store = get(userStore);
      if (!store.user) return;

      const newMetadata = {
        ...store.user.metadata,
        results_explorer: {
          ...store.user.metadata?.results_explorer,
          active: newTab,
        },
      };

      await saveUserMetadata(newMetadata);
      dispatch("showHelper", newTab);

      currentExplorer = newTab;
      dispatch("showContent", newTab);
    } catch (error) {
      logError(error, "ResultsExplorer", undefined, { message: "Error updating tab preference" });
    }
  }

  function onShowContent() {
    dispatch("showContent", currentExplorer);
  }

  function onEditCustomer() {
    dispatch("editCustomer", { customer_id });
  }

  function handleOpenSession(event: CustomEvent) {
    // Forward the event to parent to handle opening the session in chat
    dispatch("openChatSession", event.detail);
  }
</script>


<div class="results-explorer">
  {#if isCustomerLoaded && customer}
    <Tabs
      tabStyle="underline"
      contentClass="p-2 bg-gray-50 rounded-lg"
      class="tabs-container"
    >
      <TabItem
        class="results-explorer-tab"
        open={currentExplorer === "browse"}
        on:click={() => onTabChange("browse")}
      >
        <div slot="title" class="flex items-center justify-center gap-1 sub-tab">
          <FontAwesomeIcon icon={faFolderTree} class="w-4 h-4" />
          BROWSE
        </div>
        <div class="tab-content">
          <ResultsTree
            {customer_id}
            customer_code={customer.code}
            on:showContent={onShowContent}
          />
        </div>
      </TabItem>

      <TabItem
        class="results-explorer-tab"
        open={currentExplorer === "search"}
        on:click={() => onTabChange("search")}
      >
        <div slot="title" class="flex items-center justify-center gap-1 sub-tab">
          <FontAwesomeIcon icon={faMagnifyingGlass} class="w-4 h-4" />
          SEARCH
        </div>
        <div class="tab-content">
          <ResultsSearch
            {customer_id}
            customer_code={customer.code}
            on:showContent={onShowContent}
          />
        </div>
      </TabItem>

      <TabItem
        class="results-explorer-tab"
        open={currentExplorer === "your_chats"}
        on:click={() => onTabChange("your_chats")}
      >
        <div slot="title" class="flex items-center justify-center gap-1 sub-tab">
          <FontAwesomeIcon icon={faCommentDots} class="w-4 h-4" />
          YOUR CHATS
        </div>
        <div class="tab-content">
          <ChatHistory on:openSession={handleOpenSession} />
        </div>
      </TabItem>
    </Tabs>
    {#if canManageCustomers}
      <Button
        pill={true}
        outline={true}
        class="grid-header-button icon-only"
        size="sm"
        on:click={() => onEditCustomer()}
      >
        <FontAwesomeIcon icon={faPencil} />
      </Button>
    {/if}
  {:else}
    <div class="loading">Loading customer data...</div>
  {/if}
</div>
